import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Save, Plus, Trash2 } from 'lucide-react';
import { faqAPI } from '../../services/api';

const ManageFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchFAQs = useCallback(async () => {
    try {
      const data = await faqAPI.getAll({ includeUnpublished: true });
      setFaqs(data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const addFAQ = () => {
    const newFaq = {
      id: `temp-${Date.now()}`,
      question: '',
      answer: '',
      order: faqs.length + 1,
      published: true,
      isNew: true
    };
    setFaqs([...faqs, newFaq]);
  };

  const updateFAQ = (id, field, value) => {
    setFaqs(faqs.map(faq => 
      faq.id === id ? { ...faq, [field]: value, modified: true } : faq
    ));
  };

  const deleteFAQ = async (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      try {
        if (!id.startsWith('temp-')) {
          await faqAPI.delete(id);
        }
        setFaqs(faqs.filter(faq => faq.id !== id));
        alert('FAQ deleted successfully!');
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        alert('Failed to delete FAQ');
      }
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      for (const faq of faqs) {
        if (faq.isNew && faq.id.startsWith('temp-')) {
          // Create new FAQ
          await faqAPI.create({
            question: faq.question,
            answer: faq.answer,
            order: faq.order,
            published: faq.published
          });
        } else if (faq.modified) {
          // Update existing FAQ
          await faqAPI.update(faq.id, {
            question: faq.question,
            answer: faq.answer,
            order: faq.order,
            published: faq.published
          });
        }
      }
      await fetchFAQs();
      alert('FAQs saved successfully!');
    } catch (error) {
      console.error('Error saving FAQs:', error);
      alert('Failed to save FAQs');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage FAQs</h1>
          <div className="flex gap-3">
            <Button onClick={addFAQ} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
              <Plus size={20} />
              Add FAQ
            </Button>
            <Button 
              onClick={handleSaveAll} 
              disabled={saving}
              className="bg-red-700 hover:bg-red-800 flex items-center gap-2"
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {faqs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <p className="text-gray-500">No FAQs yet. Click "Add FAQ" to create one.</p>
            </div>
          ) : (
            faqs.map((faq, index) => (
              <div key={faq.id} className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-gray-800">FAQ #{index + 1}</h3>
                  <Button
                    onClick={() => deleteFAQ(faq.id)}
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question
                    </label>
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Enter question"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Answer
                    </label>
                    <textarea
                      value={faq.answer}
                      onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Enter answer"
                      rows="4"
                    />
                  </div>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={faq.published ?? true}
                      onChange={(e) => updateFAQ(faq.id, 'published', e.target.checked)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-700">Published on public site</span>
                  </label>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ManageFAQs;
